import { computed, inject, onMounted, onScopeDispose, provide, ref, shallowReactive, watch } from 'vue';
import { useConfirm } from 'primevue/useconfirm';

const settingsDraftContext = Symbol('settingsDraftContext');

/** Confirm once, and recheck busy state when the user accepts. */
export function createSettingsLeaveGuard({ confirm, isDirty, isBusy, pending = ref(false) }) {
  const requestLeave = (leave) => {
    if (pending.value || isBusy()) return;
    if (!isDirty()) { leave(); return; }
    pending.value = true;
    let settled = false;
    const finish = (accepted) => {
      if (settled) return;
      settled = true;
      pending.value = false;
      if (accepted && !isBusy()) leave();
    };
    confirm.require({
      group: 'settings-draft',
      header: 'Unsaved changes',
      message: 'Discard your unsaved changes?',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Keep editing',
      acceptLabel: 'Discard changes',
      acceptClass: 'p-button-danger',
      accept: () => finish(true),
      reject: () => finish(false),
      onHide: () => finish(false),
    });
  };
  return { pending, requestLeave };
}

export function provideSettingsDraftGuard(confirmService = useConfirm()) {
  const entries = shallowReactive(new Set());
  const isDirty = computed(() => [...entries].some((entry) => entry.isDirty.value));
  const isBusy = computed(() => [...entries].some((entry) => entry.isBusy.value));
  const guard = createSettingsLeaveGuard({
    confirm: confirmService, isDirty: () => isDirty.value, isBusy: () => isBusy.value,
  });
  provide(settingsDraftContext, {
    confirm: confirmService,
    pending: guard.pending,
    register(entry) {
      entries.add(entry);
      return () => entries.delete(entry);
    },
  });
  const beforeUnload = (event) => {
    if (!isDirty.value && !isBusy.value) return;
    event.preventDefault();
    event.returnValue = '';
  };
  onMounted(() => window.addEventListener('beforeunload', beforeUnload));
  onScopeDispose(() => window.removeEventListener('beforeunload', beforeUnload));
  return { ...guard, isDirty, isBusy };
}

/** A baseline is taken after the editor has filled its draft on open. */
export function useSettingsDraftGuard({ draft, visible = () => true, saving = () => false }) {
  const context = inject(settingsDraftContext, null);
  const snapshot = () => JSON.stringify(typeof draft === 'function' ? draft() : draft);
  const baseline = ref(snapshot());
  watch(visible, (open) => {
    if (open) baseline.value = snapshot();
  }, { flush: 'post' });
  const isDirty = computed(() => visible() && snapshot() !== baseline.value);
  const isBusy = computed(() => Boolean(saving()));
  const entry = { isDirty, isBusy };
  const unregister = context?.register(entry);
  if (unregister) onScopeDispose(unregister);
  // Outside Settings, shared forms retain their existing close behaviour.
  const guard = context
    ? createSettingsLeaveGuard({ confirm: context.confirm, pending: context.pending, isDirty: () => isDirty.value, isBusy: () => isBusy.value })
    : { requestLeave: (leave) => { if (!isBusy.value) leave(); } };
  return { ...guard, isDirty, isBusy, markSaved: () => { baseline.value = snapshot(); } };
}
